// Renders the shareable image for the daily card: bordeaux ground, the thought of the day,
// the restaurant's name and a small Assaggioo mark. 4:5 portrait (1080x1350) — it fits an
// Instagram post and looks fine as a WhatsApp status. Pure canvas, no network.

interface Opts {
  quote: string;
  restaurantName: string;
  title: string;      // e.g. "Karta e ditës"
  dateLabel: string;
}

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) { lines.push(line); line = w; } else line = test;
  }
  if (line) lines.push(line);
  return lines;
}

export async function renderDailyCardImage({ quote, restaurantName, title, dateLabel }: Opts): Promise<Blob> {
  try { await document.fonts?.load("64px 'DM Serif Display'"); await document.fonts?.load("700 36px 'DM Sans'"); } catch { /* fall back to Georgia/system */ }
  const W = 1080, H = 1350;
  const canvas = document.createElement('canvas');
  canvas.width = W; canvas.height = H;
  const ctx = canvas.getContext('2d')!;

  const g = ctx.createLinearGradient(0, 0, W, H);
  g.addColorStop(0, '#7C2D43'); g.addColorStop(0.4, '#A24960'); g.addColorStop(0.68, '#6B2737'); g.addColorStop(1, '#4E1725');
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  for (let i = 0; i < 700; i++) {
    ctx.fillStyle = Math.random() > 0.5 ? 'rgba(255,255,255,.14)' : 'rgba(0,0,0,.12)';
    ctx.fillRect(Math.random() * W, Math.random() * H, 2.4, 2.4);
  }
  ctx.strokeStyle = 'rgba(255,255,255,.28)'; ctx.lineWidth = 3;
  ctx.strokeRect(48, 48, W - 96, H - 96);

  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(251,239,242,.75)';
  ctx.font = "700 32px 'DM Sans', system-ui, sans-serif";
  (ctx as any).letterSpacing = '6px';
  ctx.fillText(title.toUpperCase(), W / 2, 170);
  (ctx as any).letterSpacing = '0px';
  ctx.font = "400 30px 'DM Sans', system-ui, sans-serif";
  ctx.fillText(dateLabel, W / 2, 218);

  // Quote: shrink until it fits in ~9 lines
  let size = 78, lines: string[] = [];
  do {
    ctx.font = `400 ${size}px 'DM Serif Display', Georgia, serif`;
    lines = wrap(ctx, quote, W - 240);
    size -= 4;
  } while (lines.length > 9 && size > 40);
  const lh = (size + 4) * 1.32;
  const blockH = lines.length * lh;
  let y = (H - blockH) / 2 + lh * 0.55 + 10;
  ctx.fillStyle = '#FBEFF2';
  for (const l of lines) { ctx.fillText(l, W / 2, y); y += lh; }

  ctx.fillStyle = 'rgba(255,255,255,.9)';
  ctx.font = "700 46px 'DM Sans', system-ui, sans-serif";
  ctx.fillText(restaurantName, W / 2, H - 236);

  // small Assaggioo mark (same geometry as AssaggiooLogo) + name
  const m = 64, mx = W / 2 - m / 2, my = H - 200, u = m / 100;
  ctx.lineCap = 'round'; ctx.lineWidth = 15 * u; ctx.strokeStyle = '#FFFFFF';
  ctx.beginPath(); ctx.moveTo(mx + 50 * u, my + 12 * u); ctx.lineTo(mx + 15 * u, my + 90 * u); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(mx + 50 * u, my + 12 * u); ctx.lineTo(mx + 85 * u, my + 90 * u); ctx.stroke();
  ctx.fillStyle = '#F59E0B';
  const bx = mx + 29 * u, by = my + 57 * u, bw = 42 * u, bh = 13 * u, r = bh / 2;
  ctx.beginPath(); ctx.moveTo(bx + r, by); ctx.lineTo(bx + bw - r, by); ctx.arc(bx + bw - r, by + r, r, -Math.PI / 2, Math.PI / 2);
  ctx.lineTo(bx + r, by + bh); ctx.arc(bx + r, by + r, r, Math.PI / 2, -Math.PI / 2); ctx.closePath(); ctx.fill();
  ctx.fillStyle = 'rgba(251,239,242,.85)';
  ctx.font = "400 40px 'DM Serif Display', Georgia, serif";
  ctx.fillText('Assaggioo', W / 2, H - 100);

  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(b => (b ? resolve(b) : reject(new Error('image failed'))), 'image/png'));
}

/** Share the card: native share sheet with the image if the device allows it, then text+link,
 *  and finally a plain download so desktop browsers still get something useful. */
export async function shareDailyCard(opts: Opts & { url: string }): Promise<'shared' | 'downloaded' | 'cancelled'> {
  const text = `“${opts.quote}” — ${opts.restaurantName}`;
  let blob: Blob | null = null;
  try { blob = await renderDailyCardImage(opts); } catch { /* text-only share below */ }
  const nav = navigator as any;
  try {
    if (blob) {
      const file = new File([blob], 'karta-e-dites.png', { type: 'image/png' });
      if (nav.canShare?.({ files: [file] })) { await nav.share({ files: [file], text }); return 'shared'; }
    }
    if (nav.share) { await nav.share({ text, url: opts.url }); return 'shared'; }
  } catch (e: any) {
    if (e?.name === 'AbortError') return 'cancelled';
  }
  if (blob) {
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob); a.download = 'karta-e-dites.png';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    return 'downloaded';
  }
  return 'cancelled';
}
